import { useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router';
import { Box, Typography, styled, InputAdornment, Menu, MenuItem } from '@mui/material';
import InputField from '../components/InputField';
import SvgIcon from '../components/SvgIcon';
import { ReactComponent as ArrowLeftIcon } from '../assets/icons/24/arrow_left.svg';
import { ReactComponent as SearchIcon } from '../assets/icons/24/search.svg';
import { ReactComponent as ArrowIcon } from '../assets/icons/24/arrow_large_white.svg';
import { THEME_MODES } from '../theme';
import { useNotifications } from '../hooks/notifications';
import { getNotificationRoute } from '../constants/notification';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';

const Body = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  height: '100%',
  margin: '0px 32px 32px 0px',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  borderRadius: 28,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor:
    theme.palette.mode === THEME_MODES.DARK ? '#232323' : theme.palette.background.default,
}));

const Breadcrumb = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: 24,
  cursor: 'pointer',
  '&:hover': { opacity: 0.8 },
});

const PageHeader = styled(Box)({
  padding: '0 24px',
});

const FilterToolbar = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 24,
  padding: '16px 24px',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
});

const TabsSwitchWrapper = styled(Box)({
  width: 'fit-content',
  padding: 1,
  borderRadius: 20,
  background: 'linear-gradient(190deg, #B9FF66 0%, #5a6b3a 75%, #2a3320 100%)',
  height: 42,
  boxSizing: 'border-box',
  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
});

const TabsSwitchContainer = styled(Box)({
  display: 'flex',
  borderRadius: 20,
  padding: 4,
  background: 'linear-gradient(to bottom, #3D4537, #3E4638), #3A4033',
});

const Tab = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$active',
})(({ $active }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '12px 16px',
  borderRadius: 20,
  height: 32,
  boxSizing: 'border-box',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: $active ? 900 : 500,
  letterSpacing: 0.06,
  textTransform: 'uppercase',
  color: $active ? '#141414' : '#FFFFFF',
  backgroundColor: $active ? '#B4FF48' : 'transparent',
  border: $active ? '1px solid #B4FF48' : 'none',
  boxShadow: $active
    ? 'inset -1px -1px 0 rgba(255,255,255,0.15), 1px 1px 2px rgba(0,0,0,0.15)'
    : 'none',
  transition: 'background-color 0.2s ease, color 0.2s ease',
  '&:hover': {
    color: $active ? '#141414' : '#FFFFFF',
  },
}));

const SearchInputWrapper = styled(Box)({
  flex: 1,
  maxWidth: 400,
});

const SORT_OPTIONS = {
  date: {
    label: 'Date',
    options: [
      { value: 'newest', label: 'Récent → Ancien' },
      { value: 'oldest', label: 'Ancien → Récent' },
    ],
  },
};

const SortButton = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$open',
})(({ $open }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 20px',
  borderRadius: 40,
  border: $open ? '1px solid #B9FF66' : '1px solid #ACACAC',
  cursor: 'pointer',
  minWidth: 140,
  width: 240,
  justifyContent: 'space-between',
  height: 44,
  boxSizing: 'border-box',
}));

const SortMenu = styled(Menu)({
  '& .MuiPaper-root': {
    background: '#303030',
    borderRadius: 14,
    marginTop: 4,
    border: '1px solid #494949',
    minWidth: 240,
  },
  '& .MuiList-root': {
    padding: 0,
  },
});

const SortMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== '$selected',
})(({ $selected }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '10px 16px',
  color: $selected ? '#B9FF66' : '#FFFFFF',
  fontWeight: $selected ? 600 : 500,
}));

const NotificationList = styled(Box)({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  padding: '0 24px 24px',
});

const NotificationRow = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  borderBottom: '1px solid #494949',
  cursor: 'pointer',
  padding: 16,
  '&:last-of-type': {
    borderBottom: 'none',
  },
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderRadius: 8,
    border: 'none',
  },
});

const NotificationsScreen = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const listRef = useRef(null);
  const {
    data: notifications,
    unreadCount: apiUnreadCount,
    loading,
    loadingMore,
    hasMore,
    loadMore,
    markAsRead,
    markAllAsRead,
  } = useNotifications(filter);

  useInfiniteScroll(listRef, {
    onLoadMore: loadMore,
    hasMore,
    loading,
    loadingMore,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [sortValue, setSortValue] = useState('newest');

  const [readOverride, setReadOverride] = useState(new Set());
  const sortOpen = Boolean(sortAnchorEl);

  const notificationsWithReadStatus = useMemo(() => {
    return notifications.map((n) => ({
      ...n,
      unread: !readOverride.has(n.id) && n.unread,
    }));
  }, [notifications, readOverride]);

  const unreadCount = Math.max(0, (apiUnreadCount ?? 0) - readOverride.size);

  const filteredNotifications = useMemo(() => {
    let list =
      filter === 'unread'
        ? notificationsWithReadStatus.filter((n) => n.unread)
        : notificationsWithReadStatus;

    const query = searchQuery.trim().toLowerCase();
    if (query) {
      list = list.filter(
        (n) =>
          (n.title || '').toLowerCase().includes(query) ||
          (n.description || '').toLowerCase().includes(query)
      );
    }

    const sorted = [...list].sort((a, b) => {
      const dateA = new Date(a.createdAt || a.date).getTime();
      const dateB = new Date(b.createdAt || b.date).getTime();
      return sortValue === 'oldest' ? dateA - dateB : dateB - dateA;
    });

    return sorted;
  }, [notificationsWithReadStatus, filter, searchQuery, sortValue]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead();
    setReadOverride((prev) => new Set([...prev, ...notifications.map((n) => n.id)]));
  };

  const handleNotificationClick = (notification) => {
    if (notification.unread) {
      markAsRead(notification.id);
      setReadOverride((prev) => new Set([...prev, notification.id]));
    }
    const route = getNotificationRoute(notification);
    if (route) {
      navigate(route);
    }
  };

  const handleSortOpen = (e) => setSortAnchorEl(e.currentTarget);
  const handleSortClose = () => setSortAnchorEl(null);
  const handleSortSelect = (value) => {
    setSortValue(value);
    setSortAnchorEl(null);
  };

  return (
    <Body>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Breadcrumb onClick={handleBack}>
          <SvgIcon icon={ArrowLeftIcon} color="#ACACAC" />
          <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#ACACAC' }}>
            CHAUFFEURS
          </Typography>
        </Breadcrumb>

        <PageHeader>
          <Typography sx={{ fontSize: 22, fontWeight: 500, letterSpacing: 0.06 }}>
            NOTIFICATIONS
          </Typography>
        </PageHeader>

        <FilterToolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
            <TabsSwitchWrapper>
              <TabsSwitchContainer>
                <Tab $active={filter === 'all'} onClick={() => setFilter('all')}>
                  TOUT
                </Tab>
                <Tab $active={filter === 'unread'} onClick={() => setFilter('unread')}>
                  NON LU ({unreadCount})
                </Tab>
              </TabsSwitchContainer>
            </TabsSwitchWrapper>

            <SearchInputWrapper>
              <InputField
                fullWidth
                placeholder="Rechercher"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  shrink: false,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-input': {
                    padding: '10px 0px',
                  },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-input': {
                    padding: '10px 0px',
                  },
                }}
              />
            </SearchInputWrapper>

            <SortButton
              $open={sortOpen}
              onClick={handleSortOpen}
              aria-haspopup="true"
              aria-expanded={sortOpen ? 'true' : undefined}
            >
              <Box
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0 }}
              >
                <Typography
                  sx={{ fontSize: 10, fontWeight: 500, color: '#FFFFFFCC', lineHeight: 1 }}
                >
                  {SORT_OPTIONS.date.label}
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#FFFFFF' }}>
                  {SORT_OPTIONS.date.options.find((o) => o.value === sortValue)?.label ??
                    'Récent → Ancien'}
                </Typography>
              </Box>
              <ArrowIcon style={{ transform: sortOpen ? '' : 'rotate(180deg)' }} />
            </SortButton>

            <SortMenu
              anchorEl={sortAnchorEl}
              open={sortOpen}
              onClose={(_, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') handleSortClose();
              }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              {SORT_OPTIONS.date.options.map(({ value, label }) => (
                <SortMenuItem
                  key={value}
                  $selected={sortValue === value}
                  onClick={() => handleSortSelect(value)}
                  disableRipple
                >
                  {label}
                </SortMenuItem>
              ))}
            </SortMenu>
          </Box>

          <Typography
            onClick={handleMarkAllAsRead}
            sx={{
              fontSize: 14,
              fontWeight: 900,
              color: 'primary.main',
              cursor: 'pointer',
            }}
          >
            TOUT MARQUER COMME LU
          </Typography>
        </FilterToolbar>

        <NotificationList ref={listRef}>
          {loading ? (
            <Typography sx={{ fontSize: 14, color: '#A9A9A9', py: 4, textAlign: 'center' }}>
              Chargement...
            </Typography>
          ) : filteredNotifications.length === 0 ? (
            <Typography sx={{ fontSize: 14, color: '#A9A9A9', py: 4, textAlign: 'center' }}>
              Aucune notification
            </Typography>
          ) : (
            <>
              {filteredNotifications.map((notification) => (
                <NotificationRow
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 2,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 1 }}>
                        {notification.title}
                      </Typography>
                      <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                        {notification.description}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#A9A9A9' }}>
                        {notification.date}
                      </Typography>
                      {notification.unread && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: 'primary.main',
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </NotificationRow>
              ))}
              {loadingMore && (
                <Typography sx={{ fontSize: 14, color: '#A9A9A9', py: 2, textAlign: 'center' }}>
                  Chargement...
                </Typography>
              )}
            </>
          )}
        </NotificationList>
      </Box>
    </Body>
  );
};

export default NotificationsScreen;
